/**
 * Minimoes — pSEO Content Generator
 *
 * Generates long-form editorial sections for programmatic SEO pages.
 * Copy rules: one promise everywhere ("give your AI a face"), awareness-matched angle per
 * kind (industry = problem-aware, use case = solution-aware, comparison = product-aware),
 * only features that exist in the product, no invented performance numbers.
 */

import { PRICES_CHECKED } from '@/data/pseo/competitor-content';

export type Section = {
  heading: string;
  body?: string;
  bullets?: string[];
  sub?: Array<{ heading: string; body: string }>;
};

export type PseoPageContent = {
  category: string;
  sections: Section[];
  faq: Array<{ question: string; answer: string }>;
};

// Shared, verifiable product facts reused by every generator.
const SETUP_STEPS = [
  'Create a free account. No credit card, and paid plans come with a 14-day trial',
  'Design your mascot in the 3D studio: look, outfit, personality, and a preset or cloned voice',
  'Teach it your business: website pages, files, plain text, Q&A pairs or Notion',
  'Turn on the actions you need: lead capture, booking, Shopify, Stripe, Slack, escalation, webhooks',
  'Test it in the Playground, then paste one snippet (or use the WordPress plugin) to go live',
];

const PRICING_BODY =
  'Minimoes has four plans. Free gives you 1 Twin and 50 AI credits per month to try it on a live site. Starter ($29/month) gives you 3 Twins, 460 credits, 3 projects, Activity and Analytics. Business ($149/month) gives you 10 Twins, 2,350 credits, unlimited projects, Actions and integrations, Notion and CSV export. Enterprise ($590/month) gives you unlimited Twins, 9,300 credits, Zendesk as a data source and full conversation export. One credit is one AI response. Annual billing gives you 2 months free, and every paid plan starts with a 14-day free trial.';

// ─── Industry generator ───────────────────────────────────────────────────────

function getIndustryContent(label: string): PseoPageContent {
  return {
    category: 'Industry',
    sections: [
      {
        heading: `Why ${label} websites are giving their AI a face`,
        body: `Visitors to ${label} websites arrive with a question and very little patience. If they cannot find the answer fast, they leave, usually without a word. The industry's answer has been the chat bubble: a grey box in the corner that most people have learned to ignore. That is changing. In September 2026, Meta launched its personal agent with a mascot called Jolly, and OpenAI introduced Dots, customizable characters for its agents. The biggest players in AI decided their agents needed a face. Researchers have studied the effect for years: chatbots with a human-like presence (a name, a mascot, a warm conversational style) tend to earn more trust and stronger purchase intent, and a stylized, cartoon-like look combined with warm behaviour can work better than a near-human face.

Minimoes brings that to ${label}. Instead of a text bubble, your visitors meet a 3D AI mascot that greets them out loud, listens when they speak, and answers from your own content. It does not invent answers from the open internet. When it is not sure, it says so and hands off to your team. And it does more than talk: it captures leads, books meetings, answers order and billing questions, and alerts your team when something matters.`,
        bullets: [
          `${label} visitors expect instant answers, at any hour`,
          'A text chat bubble blends in, while a talking character gets noticed and remembered',
          'Answers come only from your content: pages, files, Q&A and Notion',
          'Leads are captured when real intent appears, not with an interrupting pop-up',
          'Your team gets the conversation, with context, when a human is needed',
        ],
      },
      {
        heading: `What Minimoes does for ${label}`,
        body: `You go from sign-up to a live mascot in minutes, with no developer. Design its look and voice in the studio, feed it your existing content, and paste one snippet into your site. From there it works around the clock. It answers the repetitive questions your team handles every day, it detects buying intent and asks for contact details at the right moment, it books meetings through Calendly or Cal.com, and it escalates frustrated or blocked visitors to your helpdesk with the full transcript.

It also tells you where it fell short. The Suggestions tab lists the questions it could not answer, the content that is missing and the places where your documents contradict each other. Fix those, and your mascot gets better every week.`,
        bullets: [
          'Live in minutes: one snippet, a WordPress plugin, or guides for Shopify, Webflow, Framer, Wix and Squarespace',
          'Knowledge base: files, website crawl, plain text, Q&A pairs, Notion (Business) and Zendesk (Enterprise)',
          '3D studio: look, outfit, personality, plus preset multilingual voices or a voice cloned from a short recording',
          'Lead capture on detected intent: name, email and phone',
          'Booking with Calendly and Cal.com, Shopify products, orders and cart, Stripe invoices and subscriptions',
          'Escalation to Intercom, Zendesk, HubSpot, Salesforce, Jira, Zoho or Help Scout, plus Slack alerts',
          'Analytics: conversations, leads, tickets, topics, sentiment and ratings',
        ],
      },
      {
        heading: `How a mascot moves ${label} visitors forward`,
        body: `The mechanism is simple. A face and a voice get attention, and attention starts conversations a silent page never would. Once the conversation starts, the mascot keeps it useful: it answers from your content, removes the doubt that was about to send the visitor away, and offers the next step at the moment it makes sense.`,
        sub: [
          {
            heading: 'Get noticed',
            body: `A talking 3D character on a ${label} website stands out the moment the page loads. A proactive greeting after a short delay, and a different opener per page, invite visitors to ask instead of leaving.`,
          },
          {
            heading: 'Answer and qualify',
            body: 'When a visitor asks about pricing, availability or fit, the mascot answers and recognizes the intent behind the question. It then asks for contact details naturally, inside the conversation, without a form.',
          },
          {
            heading: 'Act on it',
            body: 'It books the meeting, shows the right button, checks the order or escalates to a human, so the visitor finishes what they came to do without opening another tab.',
          },
        ],
      },
      {
        heading: 'Go live in 5 steps',
        bullets: SETUP_STEPS,
      },
      {
        heading: `Minimoes pricing for ${label}`,
        body: PRICING_BODY,
      },
    ],
    faq: [],
  };
}

// ─── Use case generator ───────────────────────────────────────────────────────

function getUseCaseContent(label: string): PseoPageContent {
  return {
    category: 'Use Case',
    sections: [
      {
        heading: `${label}, with a face your visitors remember`,
        body: `Most ${label} tools still look the same: a text box in the corner. Minimoes replaces it with a 3D AI mascot that speaks, listens and acts. The job does not change. It answers questions, qualifies interest and moves people to the next step. The experience does. Visitors notice a character that greets them by voice, and they are more willing to talk to it than to fill in a form.

Under the hood, the mascot answers from your own content using retrieval-augmented generation. For each question, it looks up the most relevant part of your knowledge base and answers from it. When the answer is not there, it says so, offers a human, and logs the question so you can add it.`,
        bullets: [
          `${label} runs 24/7, not just during business hours`,
          'One mascot handles many conversations at once',
          'Every conversation is logged, with topics, sentiment and ratings',
          'Unanswered questions and contradictions in your content are flagged automatically',
          'Escalation to a human always includes the full transcript',
        ],
      },
      {
        heading: `What your mascot can do for ${label}`,
        body: `Beyond answering, the mascot acts. You decide which actions it can take and when. Each one triggers on what it detects in the conversation, so it behaves like a well-trained teammate rather than a script.`,
        bullets: [
          'Collect leads: name, email and phone when buying intent appears',
          'Suggested messages and custom buttons that appear at the right moment',
          'Booking: check availability, book and cancel with Calendly or Cal.com',
          'Shopify: browse products, track orders and check cart status',
          'Stripe: invoices, subscriptions and payment status inside the chat',
          'Escalation: tickets in Intercom, Zendesk, HubSpot, Salesforce, Jira, Zoho or Help Scout, and a live Salesforce handoff',
          'Slack alerts and custom webhooks (POST, GET or PATCH) triggered by intent',
        ],
      },
      {
        heading: `Measuring ${label} results`,
        body: 'Every number that matters is in the dashboard: conversations started, leads captured, tickets opened, topics, sentiment and visitor ratings, with CSV export on Business. Start on the Free plan, compare against your current setup for a few weeks, and decide with your own data, not ours.',
      },
      {
        heading: 'Go live in 5 steps',
        bullets: SETUP_STEPS,
      },
      {
        heading: 'Pricing',
        body: PRICING_BODY,
      },
    ],
    faq: [],
  };
}

// ─── Comparison generator ─────────────────────────────────────────────────────

function getComparisonContent(competitorLabel: string): PseoPageContent {
  return {
    category: 'Comparison',
    sections: [
      {
        heading: `Minimoes vs ${competitorLabel}: an honest comparison`,
        body: `${competitorLabel} and Minimoes solve different problems in different ways, and the right choice depends on what you want your visitors to experience. Minimoes has one clear point of view: your AI should have a face. It is a 3D mascot that speaks and listens, trained only on your content, and able to act. It captures leads, books meetings, answers Shopify and Stripe questions and escalates to humans. It installs with one snippet, on flat monthly plans with a free tier.

The table below compares the two on the points that matter most. Competitor prices and features change often. Ours were checked in ${PRICES_CHECKED}, so always confirm on their site before deciding.`,
        bullets: [
          'A stylized 3D mascot with a preset or cloned voice, built to look like your brand rather than a fake human',
          'Knowledge base: files, website crawl, plain text, Q&A pairs, Notion and Zendesk',
          'Actions built in: leads, booking, Shopify, Stripe, Slack, escalation and webhooks',
          'Deploys on websites, React, React Native and Flutter apps, and OBS livestreams',
          'Free plan, then $29, $149 or $590 per month, with a 14-day trial on paid plans',
        ],
      },
      {
        heading: 'Where Minimoes is strongest',
        sub: [
          {
            heading: 'Presence',
            body: 'A talking character gets noticed where a text bubble gets ignored. A stylized mascot is also unmistakably your brand, and it avoids the uncanny valley of near-human faces.',
          },
          {
            heading: 'Accountability',
            body: 'Answers come only from your content. Unanswered questions, missing content and contradictions are flagged. Frustrated visitors are escalated to a human with the full transcript.',
          },
          {
            heading: 'Action',
            body: 'Booking, lead capture, Shopify, Stripe, Slack and webhooks are part of the product, triggered by what the mascot detects in the conversation, with no code.',
          },
        ],
      },
      {
        heading: `When ${competitorLabel} might be the better choice`,
        body: `Be honest about your needs. If you need capabilities that are central to ${competitorLabel} and not to Minimoes, such as producing videos, a full human helpdesk inbox or deep custom flows, ${competitorLabel} may fit better, or the two can work side by side. If what you want is a branded AI front line that talks, sells and books on your site, app or stream, try Minimoes on the Free plan and compare with real traffic.`,
      },
    ],
    faq: [],
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export type PseoKind = 'industry' | 'use-case' | 'comparison';

export function getMinimoesPseoContent(
  kind: PseoKind,
  label: string,
  competitorLabel?: string
): PseoPageContent {
  if (kind === 'industry')   return getIndustryContent(label);
  if (kind === 'use-case')   return getUseCaseContent(label);
  if (kind === 'comparison') return getComparisonContent(competitorLabel ?? label);
  return getIndustryContent(label);
}
